import React, { useState, useEffect } from "react";
import "./FeedPage.css";
import { apiGet } from "../../services/api";
import { Content, CoachingPlan } from "../../types";

const TAGS = ["competitor", "product", "objection", "industry"];

const AREA_TAG_MAP: Record<string, string[]> = {
  "value articulation": ["competitor", "product"],
  "objection handling": ["objection"],
  "discovery questions": ["product"],
  "discovery": ["product"],
  "closing": ["competitor", "product"],
};

function getRecommendedTags(plan: CoachingPlan | null): string[] {
  if (!plan?.improvementAreas?.length) return [];
  const tags = new Set<string>();
  for (const area of plan.improvementAreas) {
    const areaLower = area.area.toLowerCase();
    for (const [key, mappedTags] of Object.entries(AREA_TAG_MAP)) {
      if (areaLower.includes(key)) {
        mappedTags.forEach((t) => tags.add(t));
      }
    }
  }
  return Array.from(tags);
}

export function FeedPage() {
  const [content, setContent] = useState<Content[]>([]);
  const [coachingPlan, setCoachingPlan] = useState<CoachingPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTag, setActiveTag] = useState("");
  const [search, setSearch] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadContent();
  }, [activeTag, search]);

  const loadInitialData = async () => {
    try {
      const planResult = await apiGet<CoachingPlan | { plan: null }>("/api/coaching/plan");
      if (planResult && !("plan" in planResult && planResult.plan === null)) {
        setCoachingPlan(planResult as CoachingPlan);
      }
    } catch {
      // No coaching plan — that's fine
    }
  };

  const loadContent = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("status", "published");
      if (activeTag) params.set("tag", activeTag);
      if (search) params.set("search", search);

      const result = await apiGet<{ items: Content[] }>(`/api/content?${params}`);
      setContent(result.items);
      setCurrentIndex(0);
    } catch (err) {
      console.error("Failed to load content:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSwipe = (direction: "up" | "down") => {
    if (direction === "up" && currentIndex < content.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else if (direction === "down" && currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const currentItem = content[currentIndex];
  const recommendedTags = getRecommendedTags(coachingPlan);
  const topFocus = coachingPlan?.improvementAreas?.[0]?.area;
  const recommendedContent = recommendedTags.length > 0
    ? content.filter((c) => c.tags.some((t) => recommendedTags.includes(t)))
    : [];

  return (
    <div className="protrain-feed-page">
      {/* Coaching context banner */}
      {topFocus && !bannerDismissed && (
        <div className="protrain-feed-banner">
          <span>📈 Your top focus area: <strong>{topFocus}</strong> — related content is highlighted below</span>
          <button className="protrain-feed-banner-dismiss" onClick={() => setBannerDismissed(true)} aria-label="Dismiss">✕</button>
        </div>
      )}

      {/* Recommended for You */}
      {recommendedContent.length > 0 && !activeTag && !search && (
        <div className="protrain-feed-recommended">
          <h3 className="protrain-feed-recommended-title">✨ Recommended for You</h3>
          <div className="protrain-feed-recommended-scroll">
            {recommendedContent.map((item) => (
              <button
                key={item.contentId}
                className="protrain-feed-recommended-card"
                onClick={() => {
                  const idx = content.findIndex((c) => c.contentId === item.contentId);
                  if (idx >= 0) setCurrentIndex(idx);
                }}
              >
                <span className="protrain-feed-recommended-card-title">{item.title}</span>
                <div className="protrain-feed-recommended-card-tags">
                  {item.tags.map((t) => (
                    <span key={t} className={`protrain-feed-card-tag ${recommendedTags.includes(t) ? "protrain-feed-card-tag--highlighted" : ""}`}>{t}</span>
                  ))}
                </div>
                {item.duration && <span className="protrain-feed-recommended-card-duration">{item.duration}s</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="protrain-feed-filters">
        <div className="protrain-feed-tags">
          <button
            className={`protrain-feed-tag ${!activeTag ? "protrain-feed-tag--active" : ""}`}
            onClick={() => setActiveTag("")}
          >
            All
          </button>
          {TAGS.map((tag) => (
            <button
              key={tag}
              className={`protrain-feed-tag ${activeTag === tag ? "protrain-feed-tag--active" : ""} ${recommendedTags.includes(tag) ? "protrain-feed-tag--recommended" : ""}`}
              onClick={() => setActiveTag(tag)}
            >
              {tag}
              {recommendedTags.includes(tag) && " ✨"}
            </button>
          ))}
        </div>
        <input
          className="protrain-feed-search"
          type="text"
          placeholder="Search content..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="protrain-feed-viewer">
        {loading ? (
          <div className="protrain-feed-loading">Loading content...</div>
        ) : content.length === 0 ? (
          <div className="protrain-feed-empty">No content available. Check back later.</div>
        ) : currentItem ? (
          <div className="protrain-feed-card">
            <div className="protrain-feed-card-counter">
              {currentIndex + 1} / {content.length}
            </div>
            <div className="protrain-feed-card-content">
              {currentItem.type === "video" && currentItem.mediaUrl ? (
                <video
                  className="protrain-feed-video"
                  src={currentItem.mediaUrl}
                  controls
                  playsInline
                />
              ) : (
                <div className="protrain-feed-text-content">
                  {currentItem.description || currentItem.textContent || ""}
                </div>
              )}
            </div>
            <h3 className="protrain-feed-card-title">{currentItem.title}</h3>
            <div className="protrain-feed-card-tags">
              {currentItem.tags.map((tag) => (
                <span key={tag} className={`protrain-feed-card-tag ${recommendedTags.includes(tag) ? "protrain-feed-card-tag--highlighted" : ""}`}>{tag}</span>
              ))}
            </div>
            {currentItem.duration && (
              <span className="protrain-feed-card-duration">{currentItem.duration}s</span>
            )}
            <div className="protrain-feed-nav">
              <button
                className="protrain-feed-nav-btn"
                onClick={() => handleSwipe("down")}
                disabled={currentIndex === 0}
              >
                ↑ Previous
              </button>
              <button
                className="protrain-feed-nav-btn"
                onClick={() => handleSwipe("up")}
                disabled={currentIndex >= content.length - 1}
              >
                Next ↓
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
